import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import AxiosInstance from "../Home/AxiosInstance";
import { clearAuthSession } from "../Home/auth";
import Profile from "../Home/Profile.png";
import logo from "../Home/logo.png";
import "../Home/theme.css";
import "./TraineeHeader.css";

const BASE_PATH = process.env.REACT_APP_BASE_PATH || "/als";

export default function TraineeHeader() {
  const [traineeId, setTraineeId] = useState(null);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [traineeDetails, setTraineeDetails] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [isNotificationModalOpen, setNotificationModalOpen] = useState(false);

  const dropdownRef = useRef(null);
  const notificationRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const BASE_MEDIA_URL = `${AxiosInstance.defaults.baseURL}media/`;
  const profileImageSrc = traineeDetails?.image_path
    ? `${BASE_MEDIA_URL}${traineeDetails.image_path}`
    : Profile;

  const isProfilePage = location.pathname === "/profile";

  useEffect(() => {
    const storedTraineeId = localStorage.getItem("traineeId");
    if (storedTraineeId) {
      setTraineeId(storedTraineeId);
      fetchTraineeDetails(storedTraineeId);
      fetchNotifications(storedTraineeId);
    }
  }, []);

  const fetchTraineeDetails = async (id) => {
    try {
      const response = await AxiosInstance.get(
        `companies/trainee-details/${id}`
      );
      setTraineeDetails(response.data);
    } catch (error) {
      console.error("Error fetching trainee details", error);
    }
  };

  const fetchNotifications = async (id) => {
    try {
      const res = await AxiosInstance.get(`companies/notifications/${id}/`);
      const inAppNotifications = res.data
        .filter((n) => n.delivered_via === "InApp")
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setNotifications(inAppNotifications);
    } catch (err) {
      console.error("Error fetching notifications:", err);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      await AxiosInstance.delete(`companies/notifications/${notificationId}`);
      setNotifications((prev) =>
        prev.filter((n) => n.notification_id !== notificationId)
      );
    } catch (error) {
      console.error("Error deleting notification:", error);
      alert("Failed to delete the notification. Please try again.");
    }
  };

  const toggleDropdown = () => {
    setDropdownVisible((prev) => !prev);
    setNotificationModalOpen(false);
  };

  const openNotificationModal = () => {
    setNotificationModalOpen(true);
    setDropdownVisible(false);
  };

  const closeNotificationModal = () => {
    setNotificationModalOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        !dropdownRef.current?.contains(e.target) &&
        !notificationRef.current?.contains(e.target)
      ) {
        setDropdownVisible(false);
      }
      if (
        isNotificationModalOpen &&
        notificationRef.current &&
        !notificationRef.current.contains(e.target)
      ) {
        closeNotificationModal();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isNotificationModalOpen]);

  const handleProfile = () => {
    navigate("/trainee-details");
    setDropdownVisible(false);
  };

  const handleChangePassword = () => {
    navigate("/change-password", {
      state: {
        userType: "trainee",
        userId: traineeId,
      },
    });
    setDropdownVisible(false);
  };

  const handleSignOut = () => {
    clearAuthSession();
    window.location.href = BASE_PATH;
    setDropdownVisible(false);
  };

  const fullName = traineeDetails
    ? `${traineeDetails.first_name || ""} ${
        traineeDetails.last_name || ""
      }`.trim()
    : "";

  const path = location.pathname;
  const isDashboard =
    path.startsWith("/trainee-profile") ||
    path.startsWith("/trainee-details") ||
    path.startsWith("/my-courses") ||
    path.startsWith("/my-enrollments") ||
    path.startsWith("/my-internship-applications") ||
    path.startsWith("/my-job-applications") ||
    path.startsWith("/my-saved");
  const isCourses =
    path === "/courses" || path.startsWith("/courses/");
  const isInternships =
    path === "/internships" || path.startsWith("/internships/");
  const isJobs = path === "/jobs" || path.startsWith("/jobs/");

  const catalogState = { from: "trainee-profile" };

  return (
    <header className="trainee-header">
      <div className="trainee-header-inner">
        <Link to="/trainee-profile" className="trainee-brand">
          <img src={logo} alt="Open Learnings" className="trainee-brand-logo" />
          <span className="trainee-brand-text">Open Learnings</span>
        </Link>

        <nav className="trainee-header-nav" aria-label="Learner navigation">
          <Link
            to="/trainee-profile"
            className={isDashboard ? "active" : undefined}
          >
            Dashboard
          </Link>
          <Link
            to="/courses"
            state={catalogState}
            className={isCourses ? "active" : undefined}
          >
            Courses
          </Link>
          <Link
            to="/internships"
            state={catalogState}
            className={isInternships ? "active" : undefined}
          >
            Internships
          </Link>
          <Link
            to="/jobs"
            state={catalogState}
            className={isJobs ? "active" : undefined}
          >
            Jobs
          </Link>
        </nav>

        {!isProfilePage && traineeDetails && (
          <div className="trainee-header-user" ref={dropdownRef}>
            <button
              type="button"
              className="trainee-user-btn"
              onClick={toggleDropdown}
              aria-expanded={dropdownVisible}
            >
              <span className="trainee-user-meta">
                <strong>{fullName || "Learner"}</strong>
                <small>My learning</small>
              </span>
              <img
                src={profileImageSrc}
                alt=""
                className="trainee-profile-image"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = Profile;
                }}
              />
            </button>

            {dropdownVisible && (
              <div className="trainee-dropdown">
                <button type="button" onClick={handleProfile}>
                  My Details
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDropdownVisible(false);
                    navigate("/trainee-career-preference");
                  }}
                >
                  Career Preference
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDropdownVisible(false);
                    navigate("/my-saved");
                  }}
                >
                  My Wishlist
                </button>
                <button type="button" onClick={openNotificationModal}>
                  Notifications
                  {notifications.length > 0 && (
                    <span className="trainee-notif-count">
                      {notifications.length}
                    </span>
                  )}
                </button>
                <button type="button" onClick={handleChangePassword}>
                  Change Password
                </button>
                <button
                  type="button"
                  className="trainee-signout"
                  onClick={handleSignOut}
                >
                  Sign Out
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {isNotificationModalOpen && (
        <>
          <div
            className="trainee-notif-backdrop"
            onClick={closeNotificationModal}
          />
          <aside className="trainee-notif-panel" ref={notificationRef}>
            <div className="trainee-notif-head">
              <h5>Notifications</h5>
              <button
                type="button"
                onClick={closeNotificationModal}
                aria-label="Close notifications"
              >
                ×
              </button>
            </div>
            {notifications.length === 0 ? (
              <p className="trainee-notif-empty">No notifications yet</p>
            ) : (
              <ul>
                {notifications.map((n) => (
                  <li key={n.notification_id}>
                    <span>{n.message}</span>
                    <button
                      type="button"
                      onClick={() =>
                        handleDeleteNotification(n.notification_id)
                      }
                      aria-label="Delete notification"
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>
        </>
      )}
    </header>
  );
}
